import { cn } from '@/lib/utils';
import type { SelectOption } from '@/types/common.types';

export interface RadioGroupProps {
  label?: string;
  name: string;
  options: SelectOption[];
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
  hint?: string;
  required?: boolean;
  /** Horizontal or vertical layout */
  orientation?: 'horizontal' | 'vertical';
  disabled?: boolean;
  className?: string;
}

export function RadioGroup({
  label,
  name,
  options,
  value,
  onChange,
  error,
  hint,
  required,
  orientation = 'vertical',
  disabled = false,
  className,
}: RadioGroupProps) {
  return (
    <fieldset className={cn('', className)}>
      {label && (
        <legend className="block text-sm font-medium text-slate-700 mb-2">
          {label}
          {required && (
            <span className="ml-0.5 text-red-500" aria-hidden="true">
              {' *'}
            </span>
          )}
        </legend>
      )}

      <div
        className={cn(
          'flex gap-3',
          orientation === 'vertical' ? 'flex-col' : 'flex-row flex-wrap',
        )}
        role="radiogroup"
      >
        {options.map((opt) => {
          const optId = `${name}-${String(opt.value)}`;
          const isChecked = value === opt.value;
          const isDisabled = disabled || opt.disabled;

          return (
            <label
              key={String(opt.value)}
              htmlFor={optId}
              className={cn(
                'flex items-start gap-2.5 cursor-pointer group',
                isDisabled && 'cursor-not-allowed opacity-60',
              )}
            >
              <input
                type="radio"
                id={optId}
                name={name}
                value={String(opt.value)}
                checked={isChecked}
                disabled={isDisabled}
                onChange={() => onChange?.(String(opt.value))}
                className={cn(
                  'mt-0.5 h-4 w-4 border-slate-300 text-blue-600',
                  'focus:ring-2 focus:ring-blue-500 focus:ring-offset-1',
                  'disabled:cursor-not-allowed',
                  error && 'border-red-500',
                )}
              />
              <div>
                <span className="text-sm font-medium text-slate-700 select-none">{opt.label}</span>
                {opt.description && (
                  <p className="text-xs text-slate-500 mt-0.5">{opt.description}</p>
                )}
              </div>
            </label>
          );
        })}
      </div>

      {hint && !error && <p className="mt-1.5 text-xs text-slate-500">{hint}</p>}
      {error && (
        <p role="alert" className="mt-1.5 text-xs text-red-600">
          {error}
        </p>
      )}
    </fieldset>
  );
}
